import React from 'react';
import { I18n } from '@aws-amplify/core';

import {
	BERA_REVISION_TYPE,
	type BeraJobSummaryListItem,
	type BeraRevisionOverrides,
	type BeraRevisionType
} from '@/types/Bera';
import { CloseIcon, InfoCircleIcon } from '@/assets/icons/v2';
import { useStartBeraRevision } from '@/views/Bera/_hooks';
import { ModalStyled } from '@/views/Bera/_components/styles';
import { MODAL_CLOSE_ICON_SIZE, MODAL_SMALL_WIDTH, WARNING_ICON_SIZE } from '@/views/Bera/constants';

import { RevisionOption } from './RevisionOption';
import * as S from './styles';

const DEFAULT_DESCRIPTION = 'Select the revision type';

interface ModalRevisionProps {
	open: boolean;
	report: Pick<BeraJobSummaryListItem, 'id'>;
	description?: string;
	overrides?: BeraRevisionOverrides;
	onSelectType?: (revisionType: BeraRevisionType) => void;
	onCancel: () => void;
}

export function ModalRevision({
	open,
	report,
	description = DEFAULT_DESCRIPTION,
	overrides,
	onSelectType,
	onCancel
}: Readonly<ModalRevisionProps>): JSX.Element {
	const { startRevision, isLoading } = useStartBeraRevision({ report, overrides, onSuccess: onCancel });

	function handleSelect(revisionType: BeraRevisionType) {
		if (onSelectType) {
			onSelectType(revisionType);
			return;
		}

		startRevision(revisionType);
	}

	return (
		<ModalStyled
			centered
			open={open}
			footer={null}
			keyboard={false}
			onCancel={onCancel}
			maskClosable={false}
			width={MODAL_SMALL_WIDTH}
			title={I18n.get('Revision')}
			closeIcon={<CloseIcon width={MODAL_CLOSE_ICON_SIZE} height={MODAL_CLOSE_ICON_SIZE} />}
		>
			<S.Subtitle>{I18n.get(description)}</S.Subtitle>
			<S.Warning>
				<InfoCircleIcon width={WARNING_ICON_SIZE} height={WARNING_ICON_SIZE} />
				<span>{I18n.get('Once a revision is created, the current version can no longer be edited')}</span>
			</S.Warning>
			<RevisionOption
				disabled={isLoading}
				onSelect={handleSelect}
				title="Full revision"
				revisionType={BERA_REVISION_TYPE.FULL}
				description="Replace all content with a new video"
			/>
			<RevisionOption
				disabled={isLoading}
				onSelect={handleSelect}
				title="Partial revision"
				revisionType={BERA_REVISION_TYPE.PARTIAL}
				description="Revise one or more TLIDs, replacing only the selected videos"
			/>
		</ModalStyled>
	);
}
